import type { BasePlaceEntity } from '@placer-ui/types';
import {
    getReportEntityClosingDate,
    getReportEntityFlag,
    isRestrictedArea,
} from 'core/services/report-entities-service/report-entities-service';
import { dateFormats, getSimpleFormatDate } from 'utils/date/date';
import { isAddressType } from 'utils/poi-provider/poi-provider';
import { ENCLOSING_COMPLEX_FLAGS } from 'utils/enclosing-complexes-filter/enclosing-complexes-filter';

export function getPOIStatusTextIndicator(
    poi: BasePlaceEntity,
    enclosingComplexes?: BasePlaceEntity[],
) {
    const { name, profile, is_closed } = poi;
    const flag = getReportEntityFlag(poi);
    const closingDate = getReportEntityClosingDate(poi);
    const isClosed = (!!closingDate || is_closed) && profile === 'available';
    const restrictedAreaText = `${name} is located within a restricted area. Learn more in the Trust Center.`;

    if (isClosed) {
        const suffix = closingDate
            ? `property reports through ${getSimpleFormatDate(
                  closingDate,
                  dateFormats.shortMonthYearWithNumericOrdinalContractionDayComma,
              )} or a Nearby Activity report.`
            : 'Nearby Activity, or Void Analysis reports.';
        return `${name} is closed and only supports ${suffix}`;
    }

    if (isRestrictedArea(poi) || flag === 'privacy_policy' || flag === 'privacy_concerns') {
        return restrictedAreaText;
    }

    if (isAddressType(poi)) {
        return 'Analyze nearby activity around this location or open a Void Analysis.';
    }

    if (flag && ENCLOSING_COMPLEX_FLAGS.includes(flag)) {
        const prefix = `${name} is located within`;
        const enclosingComplexName = enclosingComplexes?.[0].name;
        return enclosingComplexName
            ? `${prefix} ${enclosingComplexName} and only supports a Property report for the enclosing building, Nearby Activity, or Void Analysis reports.`
            : `${prefix} ${
                  flag === 'multi_story' ? 'a multistory building' : 'another building'
              } and only supports Nearby Activity, or Void Analysis reports`;
    }

    if (flag === 'dense_area') {
        return `Dense Area - ${name} is located within a dense area and only supports Nearby Activity, or Void Analysis reports.`;
    }

    if (flag === 'unusual_visit_volume') {
        return `Only nearby activity around ${name} can be analyzed. Open Nearby Activity or Void Analysis reports.`;
    }

    if (flag === 'low_panel') {
        return 'low_panel';
    }

    if (flag === 'unknown_flag') {
        return `${name} only supports a Nearby Activity report.`;
    }

    return undefined;
}
